<template>
  <LoadingSpinner v-if="projectsLoading || lineItemsLoading || invoiceLoading" />

  <div v-else class="edit-invoice">
    <PageHeader title="Edit Invoice" />

    <div class="mx-auto max-w-7xl">
      <InvoiceUpdateForm
        :model="invoice"
        :budget-line-items="budgetLineItems"
        :project="project"
        :save-function="updateInvoice"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import InvoiceUpdateForm from '../component/InvoiceUpdateForm.vue'
import { Invoice } from '@/models/invoice'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import InvoiceService from '@/services/InvoiceService'
import BudgetService from '@/services/BudgetService'
import ProjectService from '@/services/ProjectService'
import { useNotification } from '@kyvg/vue3-notification'
import { useRouter, useRoute } from 'vue-router'
import { computed, onUnmounted } from 'vue'



useHead({ title: 'Edit Invoice' })


const { notify } = useNotification()
const queryClient = useQueryClient()
const $router = useRouter()
const $route = useRoute()
const invoiceService = new InvoiceService()
const budgetService = new BudgetService()
const projectService = new ProjectService()

const invoiceId = $route.params.id as string || ''

const { data:invoice, isLoading:invoiceLoading } = useQuery({
  queryKey: ['invoice', invoiceId],
  queryFn: () => invoiceService.find(invoiceId)
})

const enableQuery = computed(() => invoice.value?.projectId !== '')

const { data:budgetLineItems, isLoading:lineItemsLoading } = useQuery({
  queryKey: ['LineItems', invoice.value?.projectId],
  queryFn: async () =>  await budgetService.getLineItems(invoice.value?.projectId),
  enabled: enableQuery
})

const { data:project, isLoading:projectsLoading } = useQuery({
  queryKey: ['project', invoice.value?.projectId],
  queryFn: () => projectService.find(invoice.value?.projectId),
  enabled: enableQuery
})

const { mutateAsync: updateInvoice } = useMutation({
  mutationFn: (invoice: Invoice) => invoiceService.update(invoiceId, invoice),
  onSuccess: (updatedInvoice: Invoice) => {
    queryClient.setQueryData(['invoice', invoiceId], updatedInvoice)
    queryClient.invalidateQueries({ queryKey: [ 'invoices', updatedInvoice.projectId ] })
    queryClient.invalidateQueries({ queryKey: ['firestore', 'invoices'], exact: false })
    notify({
      type: 'success',
      title: 'Invoice updated',
      text: 'Invoice updated successfully'
    })
    $router.push({ name: 'ShowInvoice', params: { id: updatedInvoice.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error updating invoice',
      text: error.message || 'An error occurred while updating the invoice'
    })
  }
})

onUnmounted(() => {
  queryClient.invalidateQueries({ queryKey: ['invoicedValue'], exact: false })
})
</script>
